import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { LandingPage } from "./marketing/LandingPage";
// EAGER, and it has to be. A boundary that lives inside the chunk it protects cannot catch that
// chunk failing to load. It imports only `react`, so the cost to the main bundle is a few hundred
// bytes. See crm/CrmErrorBoundary.tsx and crm/README.md's "Wiring it up".
import { CrmErrorBoundary } from "./crm/CrmErrorBoundary";
import { CmaAmbFlow } from "./cma/CmaAmbFlow";
import { AppHome } from "./home/AppHome";
import { LoginScreen } from "./screens/LoginScreen";
import { ResetScreen } from "./screens/ResetScreen";
import { ForgotScreen } from "./screens/ForgotScreen";
import { UsersScreen } from "./screens/UsersScreen";
import { ResultScreen } from "./screens/ResultScreen";
import { ExtensionScreen } from "./screens/ExtensionScreen";
import { BrandingScreen } from "./screens/BrandingScreen";
import { MlsAccessScreen } from "./mlsAccess/MlsAccessScreen";
import { MlsAdminScreen } from "./mlsAccess/MlsAdminScreen";
import { NtreisUsageScreen } from "./mlsAccess/NtreisUsageScreen";
import { MethodScreen } from "./screens/MethodScreen";
import { BriefsScreen } from "./briefs/BriefsScreen";
import { PropertyTimelineScreen } from "./briefs/PropertyTimelineScreen";
import { mockRecommendation } from "./mocks/recommendation.mock";
/* The guard lives in its own module so its test does not have to import this one — see the note
   at the top of that file. Re-exported here because `App.tsx` was its address for a long time. */
import { RequireAuth } from "./auth/RequireAuth";
import {
  APPRAISAL_SUPPORT_ENABLED,
  DOCUMENT_EDITOR_ENABLED,
  EXTENSION_CAPTURE_ENABLED,
  METHOD_PAGE_ENABLED,
} from "./flags";

// Lazily loaded so the CRM's code and its stylesheet cost nothing until someone opens it.
// `./crm` (the folder's index.ts) default-exports the component `React.lazy` needs.
const CrmApp = lazy(() => import("./crm"));

/* THE DOCUMENT EDITOR, LAZILY — and the laziness is a requirement, not a tidiness preference.
   `editor/EditorScreen` pulls six ProseMirror packages measured at 83.0 KiB gzip on their own
   (`docs/runs/evidence/w2-editor/bundle-delta.md`), 94.1 KiB with this module's own code and
   stylesheet. A static import would put every byte of that in the main chunk for every visitor,
   including the ones who never open a document. Measured after routing: the main chunk is
   unchanged and the editor is its own chunk. */
const EditorScreen = lazy(() => import("./editor/EditorScreen"));

/**
 * `/editor/<job id>` — KEPT AS A REDIRECT, NOT AS A SURFACE (S3-02, 2026-09-05).
 *
 * The document editor's way in is now `/cma/{id}?view=document`: the screen the agent is already
 * on, with the brief they just built on it, rather than a second address that shows the same brief
 * with different furniture around it. Two surfaces over one document is two places for a guard to
 * be added to only one of them, and the one that was missed looks exactly like the one that was
 * not.
 *
 * This route survives because links to it exist — in notes, in bookmarks, in a message somebody
 * sent a colleague — and the rule the route already followed is the right one: a withdrawn address
 * should land on a real page rather than on the blank 200 this SPA renders for an unmatched path.
 * It now lands on the BRIEF, which is better than the home page it used to land on: the agent
 * asked for a particular document and gets it.
 *
 * WITH THE FLAG OFF it redirects to the brief WITHOUT the view parameter. That is not a detail.
 * `?view=document` is a string a person can type, so flag-off has to mean the state is
 * unreachable rather than merely unoffered — `resolveView` enforces that on the receiving end, and
 * this sends nothing that would need enforcing.
 *
 * `replace`, because a redirect the back button lands on again is a trap.
 */
function EditorRouteRedirect() {
  const { jobId } = useParams<{ jobId?: string }>();
  if (jobId === undefined) return <Navigate to="/" replace />;
  const to = DOCUMENT_EDITOR_ENABLED ? `/cma/${jobId}?view=document` : `/cma/${jobId}`;
  return <Navigate to={to} replace />;
}

/* THE APPRAISAL-SUPPORT WORKSPACE, LAZILY — and the laziness is a requirement, not a tidiness
   preference. `appraisal/mock.ts` is the engine-generated fixture and is ~178 KB of JSON before a
   line of the module's own components. A static import would put every byte of it in the main
   chunk for every visitor, including the ones who never open an appraisal. Measured A/B after
   routing (`docs/runs/evidence/w3-appraisal-ui/BUNDLE-AB.md`): the main chunk is unchanged and the
   workspace is its own chunk. */
const AppraisalScreen = lazy(() => import("./appraisal/AppraisalScreen"));
// S10a (2026-09-23): `/appraisal` lists real briefs; the fixture moved to `/appraisal/_fixture`.
const AppraisalProjects = lazy(() => import("./appraisal/AppraisalProjects"));
const AppraisalFixtureGate = lazy(() => import("./appraisal/AppraisalFixtureGate"));

/**
 * `/next` root. Now routed (this session adds the first real routing — prior sessions rendered a
 * single screen with no router). ADR-0003 workstream 7's strangler order — "intake -> confirm grid
 * -> report" — is implemented at `/cma` via `CmaAmbFlow`; `/result-demo` keeps the existing
 * Recommendation-contract result screen reachable on the mock fixture for design review, since the
 * live `/api/amb/*` contract (see `cma/NarrativeReportScreen.tsx`'s docstring) does not yet expose
 * a JSON result matching that contract for a finished AMB job.
 *
 * `/login` (2026-07-27): the nav and `lib/auth.ts`'s 401 handler both pointed at `/login`, which no
 * route matched — and because the deploy rewrites every unmatched path to index.html, that rendered
 * a BLANK page rather than a 404. Signing in was impossible. The catch-all below closes the same
 * class of bug for good: an unknown path now lands on the landing page instead of nothing at all.
 *
 * UI-009 (Wave 2): `/cma/:jobId?` — the job id lives in the URL from the moment it exists, so a
 * refresh mid-extract resumes instead of dropping the user on an empty form while their job keeps
 * running server-side. The route is also auth-guarded now: it used to let an unauthenticated user
 * upload a file and wait 34 seconds before bouncing them to `/login`.
 */
export default function App() {
  // UI-051: a file dropped ANYWHERE outside the dropzone used to navigate the browser to that file
  // and take the whole SPA with it — the single most destructive near-miss in the flow.
  useEffect(() => {
    const swallow = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", swallow);
    window.addEventListener("drop", swallow);
    return () => {
      window.removeEventListener("dragover", swallow);
      window.removeEventListener("drop", swallow);
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* `/app` — THE SIGNED-IN FRONT DOOR (S03, 2026-09-22).

            Every door into this product used to land on `/cma`: `safeNextPath`'s three branches,
            both "Open the app" buttons, the shell's own header action. A signed-in user therefore
            never saw what else the product does and had no route back to a start. `/app` is that
            start. Read `home/AppHome.tsx` for what is on it and why each card is gated exactly as
            its route is.

            EAGER, not lazy like `/crm` and the editor. This is the screen people arrive at from a
            cold load and return to from the bar on every page; a chunk fetch in front of "where
            am I" is the wrong place to save a few kilobytes. It pulls `briefs/api`, which
            `/briefs` already loads eagerly for the same reason.

            Auth-guarded like every other product route: it reads `/api/me` and the shelf, both of
            which answer 401 to an anonymous caller. `RequireAuth` sends them to
            `/login?next=/app`, and `safeNextPath` now falls back here too, so the two agree. */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppHome />
            </RequireAuth>
          }
        />
        <Route path="/login" element={<LoginScreen />} />
        {/* `/reset` — PUBLIC, and it has to be: the whole point is that this person cannot sign
            in. `tests/test_base_url_single_source.py` has predicted this exact gap since August:
            "once valubrief.ai serves the /next static client, that client has no /reset route, so
            https://valubrief.ai/reset?token=... falls through the SPA rewrite to index.html and
            renders a normal-looking page that does nothing with the token." Both reset endpoints
            have been live and validating for months with nothing on this side to call them. */}
        <Route path="/reset" element={<ResetScreen />} />
        {/* `/forgot` — the self-serve half of the pair. Routed only once mail could actually
            deliver (2026-09-04); before that it would have been a form that succeeds, sends
            nothing, and leaves the person waiting for an email that does not exist. */}
        <Route path="/forgot" element={<ForgotScreen />} />
        {/* `/users` — admin only. `RequireAuth` is the session half; the ADMIN half is the
            server's, on every request. This screen renders 403s rather than data for a non-admin
            who types the URL, which is the correct outcome: hiding the nav entry is convenience,
            `require_admin` is the control. */}
        <Route
          path="/users"
          element={
            <RequireAuth>
              <UsersScreen />
            </RequireAuth>
          }
        />
        {/* It shipped for half a day as `/people` before being renamed. The catch-all below would
            send a stale link to the marketing page, which is the dead end this router's own
            docstring exists to prevent — so it redirects instead. */}
        {/* MLS ACCESS (slice mls-access-ui). Auth-guarded; with the server flag off the screens
            show one calm sentence (every route 404s). The group and usage screens are the
            server's NTREIS-manager check on every request; the nav links are convenience. */}
        <Route path="/mls-access" element={<RequireAuth><MlsAccessScreen /></RequireAuth>} />
        <Route path="/mls-access/admin" element={<RequireAuth><MlsAdminScreen /></RequireAuth>} />
        <Route path="/mls-access/usage" element={<RequireAuth><NtreisUsageScreen /></RequireAuth>} />
        <Route path="/people" element={<Navigate to="/users" replace />} />
        <Route
          path="/cma/:jobId?"
          element={
            <RequireAuth>
              <CmaAmbFlow />
            </RequireAuth>
          }
        />
        {/* The capture-token UI lived only in Apex's index.html, so an agent working here had no
            way to obtain the credential the browser extension signs in with — a live feature with
            no reachable surface. Auth-guarded like /cma: it issues a credential. */}
        {/* WITHDRAWN 2026-08-07 behind `EXTENSION_CAPTURE_ENABLED` (default off) — see
            `src/flags.ts` for why, and do not re-enable without reading that comment. The route is
            kept and redirects rather than deleted, so an agent who bookmarked it lands somewhere
            real instead of on a blank 404. */}
        <Route
          path="/extension"
          element={
            EXTENSION_CAPTURE_ENABLED ? (
              <RequireAuth>
                <ExtensionScreen />
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* REPORT BRANDING (2026-08-10). Auth-guarded like /cma: it reads and writes the agent's
            own record. A prerequisite for the apex swap rather than a convenience — the only other
            branding form lives in the legacy app, and the moment valubrief.ai serves this client
            that form is somewhere nobody looks while every brief keeps rendering from whatever it
            last saved. See screens/BrandingScreen.tsx. */}
        <Route
          path="/branding"
          element={
            <RequireAuth>
              <BrandingScreen />
            </RequireAuth>
          }
        />
        {/* THE METHOD PAGE (2026-08-15) — WITHDRAWN BEHIND `METHOD_PAGE_ENABLED`, DEFAULT OFF.
            `screens/MethodScreen.tsx` shows how one finished brief reached its number, out of that
            brief's own payload: the method paragraph, the sources table, the comparable set, how
            the figure was reached, and the disclosures. It is the web form of the section Folio
            already prints ("How this was done"), and it adds no endpoint — it reads
            `GET /api/next/amb/jobs/{id}/result`, the same call the report screen makes.

            The `:jobId?` is optional for the same reason `/cma/:jobId?`'s is: a bare `/method` is a
            URL people will type, and it lands on a real page that explains what the route is for
            rather than on a 404 the SPA would render as a blank 200.

            Gated the same way `/extension` is — the constant decides, and with it off the route
            redirects rather than 404ing, so a bookmarked link lands somewhere real. Auth-guarded
            like /cma and /branding: the result endpoint answers 401 to an anonymous caller, so
            without the guard a signed-out visitor would get this screen's error state instead of
            the sign-in page. Read `flags.ts` before flipping it on: a finished page is dark here
            because who the page is FOR has not been decided, not because it is unfinished. */}
        <Route
          path="/method/:jobId?"
          element={
            METHOD_PAGE_ENABLED ? (
              <RequireAuth>
                <MethodScreen />
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* THE CRM (2026-08-15). `client/src/crm/` has been complete, typechecked and importable
            for some time, and nothing imported it — so `/crm` fell through the catch-all below and
            silently redirected to the landing page. The server half is registered
            (`app.py` mounts `crm_next.router` at `/api/next/crm/*`) and its `/status` route always
            answers, so the island gates ITSELF on `VALUBRIEF_CRM_NEXT_ENABLED` at runtime rather
            than on a build-time constant. With the flag off this route renders the module's own
            "not enabled" state instead of a blank page — which is why routing it is safe now and
            does not wait on the flag.

            The trailing `/*` is required: CrmApp owns everything below /crm and routes it itself.

            The boundary is OUTSIDE the Suspense deliberately. Suspense covers a chunk that is
            still loading; it does nothing for a chunk that FAILS to load or a component that
            throws while rendering. Both throw during render and need a boundary — and because
            this SPA rewrites unmatched paths to index.html, an uncaught render error shows a
            blank page with HTTP 200 rather than an error. That exact failure has bitten this app
            before (see the `/login` note above). */}
        <Route
          path="/crm/*"
          element={
            /* Auth-guarded like /cma and /branding. `/api/next/crm/*` answers 401 to an anonymous
               caller, so without this an unauthenticated visitor would land on the island's error
               state instead of the sign-in page — a dead end where every other product route
               sends them somewhere they can act. */
            <RequireAuth>
              <CrmErrorBoundary title="The CRM could not be loaded">
                <Suspense fallback={<div className="vb-crm-loading">Loading the CRM…</div>}>
                  <CrmApp />
                </Suspense>
              </CrmErrorBoundary>
            </RequireAuth>
          }
        />
        {/* YOUR BRIEFS (2026-08-23). The route back to your own finished work.

            Every brief has always lived at a stable, reloadable URL and nothing has ever listed it:
            the server offered `GET /api/amb/jobs/{id}` — fetch one, if you already knew its id — and
            no list route at all. An agent who closed the tab had no route back at any layer.

            EAGER, not lazy like `/crm`. This is a destination people arrive at from a cold load and
            from the nav bar on every page, and a chunk fetch in front of "where is my work" is the
            wrong place to save 6KB. It imports the CRM's `api.ts` for `request`/`ApiError`, which
            is a types-and-fetch module and pulls none of the CRM's screens with it.

            NOT gated on `VALUBRIEF_CRM_NEXT_ENABLED`. `/reports*` carries its own server flag
            (`VALUBRIEF_REPORT_SHELF_ENABLED`, default on) precisely so this does not wait on the
            CRM cutover ADR-0004 leaves unscheduled. The screen renders its own honest state when
            the server says the shelf is off, the same way `/crm` does.

            Auth-guarded like /cma and /branding: the routes answer 401 to an anonymous caller, so
            without the guard a signed-out visitor would get an error state instead of sign-in. */}
        <Route
          path="/briefs"
          element={
            <RequireAuth>
              <BriefsScreen />
            </RequireAuth>
          }
        />
        {/* One property, every brief, oldest first — the "go back in time" ask. The key in the URL
            is the ENGINE's normalised address key, passed through exactly as the list handed it
            over; deriving it in the browser would split one property in two the first time the two
            normalisation rules disagreed about a street suffix. */}
        <Route
          path="/briefs/property/:propertyKey"
          element={
            <RequireAuth>
              <PropertyTimelineScreen />
            </RequireAuth>
          }
        />
        {/* THE DOCUMENT EDITOR — LIVE SINCE 2026-09-04, still behind `DOCUMENT_EDITOR_ENABLED` so
            it can be withdrawn in one line. `client/src/editor/` renders a brief as an editable,
            paginated document; `/editor/<job id>` opens THAT brief through
            `GET /api/next/amb/jobs/{id}/editable`, and `/editor` with no id renders a fixture and
            says so. Two limits remain and both are on the screen: page BREAKS are a browser's
            opinion of the renderer's page box, and edits reach this browser's storage and no
            server. `src/flags.ts` states the whole trade; read it before changing the constant
            either way.

            Still gated and redirected the same way `/method` and `/extension` are, so the day it
            is withdrawn a bookmarked link lands on a real page rather than on the blank 200 this
            SPA renders for an unmatched path. Auth-guarded like every other product route — the
            job call answers 401 to an anonymous caller.

            The boundary is OUTSIDE the Suspense for the reason `/crm` documents above — Suspense
            covers a chunk that is loading and does nothing for one that fails to load. */}
        {/* A REAL BRIEF GOES TO THE BRIEF'S OWN SCREEN. See `EditorRouteRedirect`. */}
        <Route path="/editor/:jobId" element={<EditorRouteRedirect />} />
        {/* `/editor` WITH NO ID IS THE FIXTURE HARNESS AND ONLY THAT. It renders a made-up brief
            and says so on its face; there is no job for it to redirect to, and there is nothing on
            it a redirect would preserve. Still gated, still auth-guarded, still behind its own
            error boundary — the boundary OUTSIDE the Suspense for the reason `/crm` documents
            above: Suspense covers a chunk that is loading and does nothing for one that fails to
            load. */}
        <Route
          path="/editor"
          element={
            DOCUMENT_EDITOR_ENABLED ? (
              <RequireAuth>
                <CrmErrorBoundary title="The document editor could not be loaded">
                  <Suspense fallback={<div className="vb-crm-loading">Loading the editor…</div>}>
                    <EditorScreen />
                  </Suspense>
                </CrmErrorBoundary>
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* THE APPRAISAL-SUPPORT WORKSPACE (2026-09-01) — WITHDRAWN BEHIND
            `APPRAISAL_SUPPORT_ENABLED`, DEFAULT OFF. `client/src/appraisal/` renders the Wave 2
            appraisal module: the sales comparison grid with every cell carrying its provenance,
            the Texas equal-and-uniform median with its sort and its pick both shown, the IAAO
            ratio-study diagnostics, and the register of thirteen analyses the module refuses to
            perform. It is dark because it renders a FIXTURE rather than a job, because no quoted
            legal text in it has been hand-checked against its primary document, and because two
            statutory ambiguities on it are open on purpose. All three are stated in `src/flags.ts`
            at length and on the screen itself; read that constant before flipping it on.

            Gated and redirected the same way `/method`, `/extension` and `/editor` are, so a
            bookmarked link lands on a real page rather than on the blank 200 this SPA renders for
            an unmatched path. Auth-guarded like every other product route: the day it reads a real
            job, that call answers 401 to an anonymous caller.

            The boundary is OUTSIDE the Suspense for the reason `/crm` documents above — Suspense
            covers a chunk that is loading and does nothing for one that fails to load. */}
        {/* `/appraisal/:jobId` draws the brief the id names; `/appraisal` draws the harness
            fixture. One element serves both — `AppraisalScreen` reads the param and never falls
            back from one to the other, which is the defect `flags.ts` describes. */}
        <Route
          path="/appraisal/:jobId"
          element={
            APPRAISAL_SUPPORT_ENABLED ? (
              <RequireAuth>
                <CrmErrorBoundary title="The appraisal workspace could not be loaded">
                  <Suspense fallback={<div className="vb-crm-loading">Loading the workspace…</div>}>
                    <AppraisalScreen />
                  </Suspense>
                </CrmErrorBoundary>
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* `/appraisal/_fixture` — THE ENGINEERING FIXTURE, MOVED HERE INTACT (S10a, 2026-09-23). It
            used to be `/appraisal` itself, so every user who pressed the home card was shown an
            invented Plano subject under a "this renders a fixture" banner. It is a test instrument
            and is kept whole; `AppraisalFixtureGate` serves it to admins and dev builds only and
            sends everyone else to the list. A static segment outranks `:jobId`, so `_fixture` is
            never read as a brief id. */}
        <Route
          path="/appraisal/_fixture"
          element={
            APPRAISAL_SUPPORT_ENABLED ? (
              <RequireAuth>
                <CrmErrorBoundary title="The appraisal workspace could not be loaded">
                  <Suspense fallback={<div className="vb-crm-loading">Loading the workspace…</div>}>
                    <AppraisalFixtureGate />
                  </Suspense>
                </CrmErrorBoundary>
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/appraisal"
          element={
            APPRAISAL_SUPPORT_ENABLED ? (
              <RequireAuth>
                <CrmErrorBoundary title="Your appraisal briefs could not be loaded">
                  <Suspense fallback={<div className="vb-crm-loading">Loading your briefs…</div>}>
                    <AppraisalProjects />
                  </Suspense>
                </CrmErrorBoundary>
              </RequireAuth>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/result-demo" element={<ResultScreen reco={mockRecommendation} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export { RequireAuth };
